/**
 * Reine Logik der oeffentlichen Melderseite (#/freimeldung/<token>).
 *
 * Kein React, kein Supabase: Texte in DE/EN, Formularpruefung,
 * Firmenvorschlaege, Fehlertexte zu den SQLSTATEs der RPC, sessionStorage-
 * Schluessel und -Serialisierung, Datumsformat. Alles, was die Seite an
 * Entscheidungen trifft, steht hier und ist in freimeldungLogic.test.ts
 * abgedeckt — die Komponenten bleiben duenn (kein jsdom im Test).
 * Vorbild: components/sige-einweisung/public/einweisungTeilnahmeLogic.ts.
 *
 * Die Seite wird vom Handy aus geoeffnet (QR im PDF). Sie verraet NIE, ob es
 * den Bericht gibt: unbekannter Token, abgelaufener Link und fehlende
 * Migration bekommen denselben Satz.
 */

import type { FreimeldungPublicItem, FreimeldungPublicPlan } from '../../../services/freimeldungPublicService';
import { pinsAufPlan, planIdsVon, type PlanPin } from '../../../services/planPins';

export type Sprache = 'de' | 'en';

/** Laengen aus den CHECKs der Tabelle inspection_item_freimeldungen (Migration Nr. 108). */
export const MAX_FIRMA = 120;
export const MAX_NAME = 120;
export const MAX_EMAIL = 200;
export const MAX_PHONE = 50;
export const MAX_KOMMENTAR = 2000;

/**
 * Platzhalter im Firmen-Auswahlfeld fuer „Andere Firma …". Kein moeglicher
 * Firmenname (die RPC trimmt und verlangt 1–120 Zeichen), damit die freie
 * Eingabe nie mit einem Vorschlag kollidiert.
 */
export const ANDERE_FIRMA = '__andere__';

/**
 * Was der Melder abgibt (Spalte `art`, Nr. 184): eine Freimeldung („behoben")
 * oder eine Zurueckweisung mit Begruendung (Betreiber 28.10.2026). Beides
 * schliesst nichts — der Koordinator entscheidet.
 */
export type MeldeArt = 'behoben' | 'zurueckgewiesen';

/** Mindestlaenge der Begruendung einer Zurueckweisung — dieselbe Grenze wie in Nr. 185. */
export const MIN_BEGRUENDUNG = 10;

/**
 * Wert der Firmen-Schnellauswahl fuer Maengel ohne Zustaendigkeit. Kein
 * moeglicher Firmenname (siehe ANDERE_FIRMA).
 */
export const OHNE_FIRMA = '__ohne__';

// ─── Texte ───────────────────────────────────────────────────────────────────

export interface FreimeldungTexte {
  titel: string;
  bericht: (nr: number, datum: string) => string;
  ladeText: string;
  ungueltigTitel: string;
  ungueltig: string;
  ladeFehlerTitel: string;
  ladeFehlerHinweis: string;

  listeUeberschrift: string;
  listeHinweis: string;
  leereListe: string;
  bereitsGemeldet: (n: number) => string;
  knopfMelden: string;
  knopfZurueckweisen: string;
  filterTitel: string;
  filterAlle: (n: number) => string;
  filterOhne: string;
  leereListeFirma: string;
  ort: string;
  firmaFeld: string;
  frist: string;
  sofortmassnahme: string;
  ohneFrist: string;

  planZeigen: string;
  planZeigenMehrere: (n: number) => string;
  planVon: (i: number, n: number) => string;
  planLaedt: string;
  planFehler: string;
  planGanz: string;
  planGroesser: string;
  planKleiner: string;
  planSchliessen: string;
  planVorheriger: string;
  planNaechster: string;

  formularTitel: string;
  formularTitelZurueck: string;
  zurueckHinweis: string;
  labelBegruendung: string;
  begruendungPlatzhalter: string;
  absendenZurueck: string;
  labelFirma: string;
  firmaBitteWaehlen: string;
  andereFirmaOption: string;
  labelAndereFirma: string;
  labelName: string;
  labelEmail: string;
  labelTelefon: string;
  labelKommentar: string;
  kommentarPlatzhalter: string;
  labelFoto: string;
  pflicht: string;
  optional: string;
  fotoWaehlen: string;
  fotoErsetzen: string;
  fotoEntfernen: string;
  fotoLaedt: string;
  fotoBereit: string;
  absenden: string;
  sendet: string;
  zurueck: string;
  hinweisPruefung: string;

  erfolgTitel: string;
  erfolgText: string;
  erfolgTitelZurueck: string;
  erfolgTextZurueck: string;
  bereitsTitel: string;
  bereitsText: string;
  gemeldetAm: string;
  weiterenMelden: string;
  fensterSchliessen: string;

  fehlerFirma: string;
  fehlerFirmaLang: string;
  fehlerName: string;
  fehlerNameLang: string;
  fehlerEmail: string;
  fehlerEmailLang: string;
  fehlerTelefonLang: string;
  fehlerKommentarLang: string;
  fehlerFotoPflicht: string;
  fehlerBegruendung: string;
  fehlerSammel: string;

  fehlerErledigt: string;
  fehlerZuViele: string;
  fehlerAllgemein: string;
  fotoNichtEingerichtet: string;
  fotoFehlgeschlagen: string;
}

const DE: FreimeldungTexte = {
  titel: 'Mangel als behoben melden',
  bericht: (nr, datum) => `Begehungsbericht Nr. ${nr}${datum ? ` vom ${datum}` : ''}`,
  ladeText: 'Mängel werden geladen…',
  ungueltigTitel: 'Dieser Link ist nicht mehr gültig',
  ungueltig: 'Dieser Link ist nicht mehr gültig.',
  ladeFehlerTitel: 'Die Seite konnte nicht geladen werden',
  ladeFehlerHinweis: 'Bitte die Internetverbindung prüfen und die Seite neu laden.',

  listeUeberschrift: 'Offene Mängel dieses Berichts',
  listeHinweis: 'Bitte den Mangel auswählen, der behoben wurde — oder ihn mit Begründung zurückweisen.',
  leereListe: 'Alle Mängel dieses Berichts sind erledigt.',
  bereitsGemeldet: (n) => `bereits gemeldet (${n})`,
  knopfMelden: 'Als behoben melden',
  knopfZurueckweisen: 'Zurückweisen',
  filterTitel: 'Ihre Firma',
  filterAlle: (n) => `Alle (${n})`,
  filterOhne: 'Ohne Firma',
  leereListeFirma: 'Für diese Auswahl stehen keine offenen Mängel in diesem Bericht.',
  ort: 'Ort',
  firmaFeld: 'Firma',
  frist: 'Frist',
  sofortmassnahme: 'Sofortmaßnahme',
  ohneFrist: 'ohne Frist',

  planZeigen: 'Auf Plan zeigen',
  planZeigenMehrere: (n) => `Auf Plan zeigen (${n} Pläne)`,
  planVon: (i, n) => `Plan ${i} von ${n}`,
  planLaedt: 'Plan wird geladen…',
  planFehler: 'Der Plan konnte nicht geladen werden.',
  planGanz: 'Ganzer Plan',
  planGroesser: 'Vergrößern',
  planKleiner: 'Verkleinern',
  planSchliessen: 'Schließen',
  planVorheriger: 'Vorheriger Plan',
  planNaechster: 'Nächster Plan',

  formularTitel: 'Mangel als behoben melden',
  formularTitelZurueck: 'Mangel zurückweisen',
  zurueckHinweis: 'Der Mangel betrifft nicht Ihre Firma oder ist kein Mangel? Bitte begründen — der Koordinator prüft die Zurückweisung.',
  labelBegruendung: 'Begründung',
  begruendungPlatzhalter: 'Warum weisen Sie den Mangel zurück?',
  absendenZurueck: 'Zurückweisung absenden',
  labelFirma: 'Firma',
  firmaBitteWaehlen: 'Bitte auswählen …',
  andereFirmaOption: 'Andere Firma …',
  labelAndereFirma: 'Firma (Eingabe)',
  labelName: 'Name',
  labelEmail: 'E-Mail (optional)',
  labelTelefon: 'Telefon (optional)',
  labelKommentar: 'Kommentar',
  kommentarPlatzhalter: 'Was wurde gemacht?',
  labelFoto: 'Beweisfoto',
  pflicht: 'Pflicht',
  optional: 'optional',
  fotoWaehlen: 'Foto aufnehmen oder auswählen',
  fotoErsetzen: 'Anderes Foto wählen',
  fotoEntfernen: 'Foto entfernen',
  fotoLaedt: 'Foto wird hochgeladen…',
  fotoBereit: 'Foto ist hochgeladen.',
  absenden: 'Meldung absenden',
  sendet: 'Meldung wird gesendet…',
  zurueck: 'Zurück zur Liste',
  hinweisPruefung: 'Die Prüfung erfolgt durch den Koordinator.',

  erfolgTitel: 'Meldung eingegangen',
  erfolgText: 'Vielen Dank — die Meldung ist eingegangen.',
  erfolgTitelZurueck: 'Zurückweisung eingegangen',
  erfolgTextZurueck: 'Vielen Dank — die Zurückweisung ist eingegangen.',
  bereitsTitel: 'Bereits gemeldet',
  bereitsText: 'Diese Meldung liegt bereits vor.',
  gemeldetAm: 'Gemeldet am',
  weiterenMelden: 'Weiteren Mangel melden',
  fensterSchliessen: 'Du kannst dieses Fenster jetzt schließen.',

  fehlerFirma: 'Bitte die Firma angeben.',
  fehlerFirmaLang: `Die Firma darf höchstens ${MAX_FIRMA} Zeichen lang sein.`,
  fehlerName: 'Bitte den Namen eintragen.',
  fehlerNameLang: `Der Name darf höchstens ${MAX_NAME} Zeichen lang sein.`,
  fehlerEmail: 'Bitte eine gültige E-Mail-Adresse eintragen oder das Feld leer lassen.',
  fehlerEmailLang: `Die E-Mail-Adresse darf höchstens ${MAX_EMAIL} Zeichen lang sein.`,
  fehlerTelefonLang: `Die Telefonnummer darf höchstens ${MAX_PHONE} Zeichen lang sein.`,
  fehlerKommentarLang: `Der Kommentar darf höchstens ${MAX_KOMMENTAR} Zeichen lang sein.`,
  fehlerFotoPflicht: 'Für diesen Bericht ist ein Beweisfoto verpflichtend.',
  fehlerBegruendung: `Bitte die Zurückweisung begründen (mindestens ${MIN_BEGRUENDUNG} Zeichen).`,
  fehlerSammel: 'Bitte die markierten Felder prüfen.',

  fehlerErledigt: 'Dieser Mangel ist inzwischen erledigt.',
  fehlerZuViele: 'Zu viele Meldungen, bitte später erneut.',
  fehlerAllgemein: 'Die Meldung konnte nicht gespeichert werden. Bitte später erneut versuchen.',
  fotoNichtEingerichtet: 'Foto-Upload ist noch nicht eingerichtet.',
  fotoFehlgeschlagen: 'Das Foto konnte nicht hochgeladen werden.',
};

const EN: FreimeldungTexte = {
  titel: 'Report defect as fixed',
  bericht: (nr, datum) => `Site inspection report no. ${nr}${datum ? ` of ${datum}` : ''}`,
  ladeText: 'Loading defects…',
  ungueltigTitel: 'This link is no longer valid',
  ungueltig: 'This link is no longer valid.',
  ladeFehlerTitel: 'The page could not be loaded',
  ladeFehlerHinweis: 'Please check your internet connection and reload the page.',

  listeUeberschrift: 'Open defects in this report',
  listeHinweis: 'Please select the defect that has been fixed — or reject it with a reason.',
  leereListe: 'All defects in this report have been completed.',
  bereitsGemeldet: (n) => `already reported (${n})`,
  knopfMelden: 'Report as fixed',
  knopfZurueckweisen: 'Reject',
  filterTitel: 'Your company',
  filterAlle: (n) => `All (${n})`,
  filterOhne: 'No company',
  leereListeFirma: 'There are no open defects in this report for this selection.',
  ort: 'Location',
  firmaFeld: 'Company',
  frist: 'Due date',
  sofortmassnahme: 'Immediate action',
  ohneFrist: 'no due date',

  planZeigen: 'Show on plan',
  planZeigenMehrere: (n) => `Show on plan (${n} plans)`,
  planVon: (i, n) => `Plan ${i} of ${n}`,
  planLaedt: 'Loading plan…',
  planFehler: 'The plan could not be loaded.',
  planGanz: 'Whole plan',
  planGroesser: 'Zoom in',
  planKleiner: 'Zoom out',
  planSchliessen: 'Close',
  planVorheriger: 'Previous plan',
  planNaechster: 'Next plan',

  formularTitel: 'Report defect as fixed',
  formularTitelZurueck: 'Reject defect',
  zurueckHinweis: 'The defect does not concern your company or is not a defect? Please give a reason — the coordinator will review the rejection.',
  labelBegruendung: 'Reason',
  begruendungPlatzhalter: 'Why do you reject the defect?',
  absendenZurueck: 'Submit rejection',
  labelFirma: 'Company',
  firmaBitteWaehlen: 'Please select …',
  andereFirmaOption: 'Other company …',
  labelAndereFirma: 'Company (free text)',
  labelName: 'Name',
  labelEmail: 'E-mail (optional)',
  labelTelefon: 'Phone (optional)',
  labelKommentar: 'Comment',
  kommentarPlatzhalter: 'What was done?',
  labelFoto: 'Photo as proof',
  pflicht: 'required',
  optional: 'optional',
  fotoWaehlen: 'Take or choose a photo',
  fotoErsetzen: 'Choose another photo',
  fotoEntfernen: 'Remove photo',
  fotoLaedt: 'Uploading photo…',
  fotoBereit: 'Photo uploaded.',
  absenden: 'Submit report',
  sendet: 'Sending report…',
  zurueck: 'Back to the list',
  hinweisPruefung: 'The coordinator will review your report.',

  erfolgTitel: 'Report received',
  erfolgText: 'Thank you — your report has been received.',
  erfolgTitelZurueck: 'Rejection received',
  erfolgTextZurueck: 'Thank you — your rejection has been received.',
  bereitsTitel: 'Already reported',
  bereitsText: 'This report has already been submitted.',
  gemeldetAm: 'Reported on',
  weiterenMelden: 'Report another defect',
  fensterSchliessen: 'You can close this window now.',

  fehlerFirma: 'Please enter the company.',
  fehlerFirmaLang: `The company must not exceed ${MAX_FIRMA} characters.`,
  fehlerName: 'Please enter your name.',
  fehlerNameLang: `The name must not exceed ${MAX_NAME} characters.`,
  fehlerEmail: 'Please enter a valid e-mail address or leave the field empty.',
  fehlerEmailLang: `The e-mail address must not exceed ${MAX_EMAIL} characters.`,
  fehlerTelefonLang: `The phone number must not exceed ${MAX_PHONE} characters.`,
  fehlerKommentarLang: `The comment must not exceed ${MAX_KOMMENTAR} characters.`,
  fehlerFotoPflicht: 'A photo as proof is required for this report.',
  fehlerBegruendung: `Please give a reason for the rejection (at least ${MIN_BEGRUENDUNG} characters).`,
  fehlerSammel: 'Please check the highlighted fields.',

  fehlerErledigt: 'This defect has been completed in the meantime.',
  fehlerZuViele: 'Too many reports, please try again later.',
  fehlerAllgemein: 'The report could not be saved. Please try again later.',
  fotoNichtEingerichtet: 'Photo upload is not set up yet.',
  fotoFehlgeschlagen: 'The photo could not be uploaded.',
};

export const TEXTE: Record<Sprache, FreimeldungTexte> = { de: DE, en: EN };

/** Texte zur Sprache des Projekts; alles Unbekannte faellt auf Deutsch zurueck. */
export const texteFuer = (lang: Sprache | string | null | undefined): FreimeldungTexte =>
  lang === 'en' ? EN : DE;

// ─── Formular ────────────────────────────────────────────────────────────────

/** Eingaben des Melders. Das Foto steht nur als hochgeladene URL drin, nie als DataURL. */
export interface FreimeldungFormular {
  firma: string;
  name: string;
  email: string;
  phone: string;
  kommentar: string;
  fotoUrl: string | null;
}

export const LEERES_FORMULAR: FreimeldungFormular = {
  firma: '',
  name: '',
  email: '',
  phone: '',
  kommentar: '',
  fotoUrl: null,
};

export type FreimeldungFeldFehler = Partial<Record<keyof FreimeldungFormular, string>>;

/** Absichtlich grob: die Adresse wird nicht zugestellt, sie ist nur ein Rueckkanal. */
const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Prueft je Feld. Die Grenzen sind die CHECKs der Tabelle — was hier
 * durchgeht, nimmt die RPC an (22023 bleibt der Notausgang fuer einen
 * veralteten Client).
 *
 * Eine ZURUECKWEISUNG verlangt die Begruendung (mindestens
 * MIN_BEGRUENDUNG Zeichen) und nie ein Foto: die Foto-Pflicht des Projekts
 * gilt dem Nachweis einer Erledigung, nicht einem Widerspruch.
 */
export const pruefeFormularFelder = (
  eingabe: FreimeldungFormular,
  fotoPflicht: boolean,
  lang: Sprache = 'de',
  art: MeldeArt = 'behoben',
): FreimeldungFeldFehler => {
  const t = texteFuer(lang);
  const fehler: FreimeldungFeldFehler = {};
  const firma = (eingabe.firma || '').trim();
  const name = (eingabe.name || '').trim();
  const email = (eingabe.email || '').trim();
  const phone = (eingabe.phone || '').trim();
  const kommentar = (eingabe.kommentar || '').trim();

  if (!firma) fehler.firma = t.fehlerFirma;
  else if (firma.length > MAX_FIRMA) fehler.firma = t.fehlerFirmaLang;

  if (!name) fehler.name = t.fehlerName;
  else if (name.length > MAX_NAME) fehler.name = t.fehlerNameLang;

  if (email) {
    if (email.length > MAX_EMAIL) fehler.email = t.fehlerEmailLang;
    else if (!EMAIL_MUSTER.test(email)) fehler.email = t.fehlerEmail;
  }

  if (phone.length > MAX_PHONE) fehler.phone = t.fehlerTelefonLang;
  if (kommentar.length > MAX_KOMMENTAR) fehler.kommentar = t.fehlerKommentarLang;
  else if (art === 'zurueckgewiesen' && kommentar.length < MIN_BEGRUENDUNG) fehler.kommentar = t.fehlerBegruendung;
  if (art !== 'zurueckgewiesen' && fotoPflicht && !(eingabe.fotoUrl || '').trim()) {
    fehler.fotoUrl = t.fehlerFotoPflicht;
  }

  return fehler;
};

/** Alle Meldungen in Feldreihenfolge; leer = Formular in Ordnung. */
export const pruefeFormular = (
  eingabe: FreimeldungFormular,
  fotoPflicht: boolean,
  lang: Sprache = 'de',
  art: MeldeArt = 'behoben',
): string[] => {
  const fehler = pruefeFormularFelder(eingabe, fotoPflicht, lang, art);
  const reihenfolge: (keyof FreimeldungFormular)[] = ['firma', 'name', 'email', 'phone', 'kommentar', 'fotoUrl'];
  return reihenfolge.flatMap((k) => (fehler[k] ? [fehler[k] as string] : []));
};

// ─── Liste ───────────────────────────────────────────────────────────────────

/**
 * Vorschlaege fuer das Firmenfeld: die Zustaendigen der offenen Maengel plus
 * die Firmenliste der RPC, ohne Doppel und alphabetisch. „Andere Firma …"
 * haengt die Oberflaeche als letzten Eintrag an (Platzhalter ANDERE_FIRMA).
 */
export const firmenAuswahl = (
  items: readonly Pick<FreimeldungPublicItem, 'responsiblePerson'>[] | null | undefined,
  firmen: readonly string[] | null | undefined,
): string[] => {
  const gesehen = new Map<string, string>();
  const nimm = (wert: unknown): void => {
    const s = typeof wert === 'string' ? wert.trim() : '';
    if (!s) return;
    const schluessel = s.toLocaleLowerCase('de');
    if (!gesehen.has(schluessel)) gesehen.set(schluessel, s);
  };
  (items || []).forEach((i) => nimm(i?.responsiblePerson));
  (firmen || []).forEach(nimm);
  return [...gesehen.values()].sort((a, b) => a.localeCompare(b, 'de'));
};

// ─── Firmen-Schnellauswahl ───────────────────────────────────────────────────

/** Ein Knopf der Schnellauswahl über der Liste. */
export interface FirmenKnopf {
  /** Firmenname (erste Schreibweise im Bericht) oder OHNE_FIRMA. */
  wert: string;
  anzahl: number;
}

const firmaSchluessel = (wert: unknown): string =>
  typeof wert === 'string' ? wert.trim().toLocaleLowerCase('de') : '';

/**
 * Die Schnellauswahl „Ihre Firma" (Betreiber 28.10.2026: wer den QR-Code
 * scannt, soll nur seine eigenen Maengel sehen). Je zustaendiger Firma ein
 * Knopf mit der Zahl ihrer offenen Maengel, alphabetisch; Maengel ohne
 * Zustaendigkeit unter OHNE_FIRMA am Ende. Gross-/Kleinschreibung und
 * Leerraum am Rand zaehlen nicht — „Elektro Meier " und „elektro meier" sind
 * eine Firma.
 *
 * Leer, wenn es nichts auszuwaehlen gibt (weniger als zwei Knoepfe): eine
 * Auswahl mit nur einer Moeglichkeit waere ein Klick ohne Wirkung.
 */
export const firmenKnoepfe = (
  items: readonly Pick<FreimeldungPublicItem, 'responsiblePerson'>[] | null | undefined,
): FirmenKnopf[] => {
  const je = new Map<string, FirmenKnopf>();
  let ohne = 0;
  (items || []).forEach((i) => {
    const schluessel = firmaSchluessel(i?.responsiblePerson);
    if (!schluessel) {
      ohne += 1;
      return;
    }
    const da = je.get(schluessel);
    if (da) da.anzahl += 1;
    else je.set(schluessel, { wert: (i.responsiblePerson || '').trim(), anzahl: 1 });
  });
  const knoepfe = [...je.values()].sort((a, b) => a.wert.localeCompare(b.wert, 'de'));
  if (ohne > 0) knoepfe.push({ wert: OHNE_FIRMA, anzahl: ohne });
  return knoepfe.length >= 2 ? knoepfe : [];
};

/** Die Maengel zur Auswahl; `null` = alle. */
export const filtereNachFirma = <T extends Pick<FreimeldungPublicItem, 'responsiblePerson'>>(
  items: readonly T[] | null | undefined,
  auswahl: string | null,
): T[] => {
  const liste = [...(items || [])];
  if (auswahl == null) return liste;
  if (auswahl === OHNE_FIRMA) return liste.filter((i) => !firmaSchluessel(i?.responsiblePerson));
  const ziel = firmaSchluessel(auswahl);
  return liste.filter((i) => firmaSchluessel(i?.responsiblePerson) === ziel);
};

/**
 * Die Auswahl, die WIRKLICH gilt. Ohne Knoepfe (weniger als zwei
 * Moeglichkeiten) oder wenn die gewaehlte Firma keinen Knopf mehr hat (ihr
 * letzter offener Mangel ist beim Nachladen weggefallen), gilt „Alle" —
 * sonst stuende eine leere Liste da, und der Knopf zum Zuruecksetzen waere
 * mit der Auswahl verschwunden. Liefert die Schreibweise des Knopfs.
 */
export const wirksameFirmenAuswahl = (auswahl: string | null, knoepfe: readonly FirmenKnopf[]): string | null => {
  if (auswahl == null || knoepfe.length === 0) return null;
  if (auswahl === OHNE_FIRMA) return knoepfe.some((k) => k.wert === OHNE_FIRMA) ? OHNE_FIRMA : null;
  const schluessel = firmaSchluessel(auswahl);
  const knopf = knoepfe.find((k) => k.wert !== OHNE_FIRMA && firmaSchluessel(k.wert) === schluessel);
  return knopf ? knopf.wert : null;
};

/**
 * Die Auswahl beim Oeffnen: die Firma aus dem Kontaktspeicher, wenn sie einen
 * Knopf hat — wer schon einmal gemeldet hat, sieht gleich wieder nur seine
 * Maengel. Sonst alle.
 */
export const startFirmenAuswahl = (firma: string | null | undefined, knoepfe: readonly FirmenKnopf[]): string | null => {
  const schluessel = firmaSchluessel(firma);
  if (!schluessel) return null;
  const knopf = knoepfe.find((k) => k.wert !== OHNE_FIRMA && firmaSchluessel(k.wert) === schluessel);
  return knopf ? knopf.wert : null;
};

/** Liegt zu diesem Mangel schon eine offene Meldung vor? */
export const istBereitsGemeldet = (item: Pick<FreimeldungPublicItem, 'offeneMeldungen'> | null | undefined): boolean =>
  (item?.offeneMeldungen ?? 0) > 0;

/** „#12" — ohne Ticketnummer bleibt die Zeile leer statt „#null". */
export const nummerText = (item: Pick<FreimeldungPublicItem, 'ticketNumber'>): string =>
  item?.ticketNumber == null ? '' : `#${item.ticketNumber}`;

/** Frist oder „Sofortmaßnahme" — der Melder soll die Dringlichkeit sehen. */
export const fristText = (
  item: Pick<FreimeldungPublicItem, 'deadline' | 'immediateAction'>,
  lang: Sprache = 'de',
): string => {
  const t = texteFuer(lang);
  if (item?.immediateAction) return t.sofortmassnahme;
  const datum = formatDatum(item?.deadline, lang);
  return datum || t.ohneFrist;
};

// ─── Plan ────────────────────────────────────────────────────────────────────

/** Ein Plan mit den Nadeln EINES Mangels (docs/freimeldung-plan-anzeige-plan.md). */
export interface FreimeldungPlanAnsicht {
  plan: FreimeldungPublicPlan;
  /** Nadeln dieses Mangels auf dem Plan, in gespeicherter Reihenfolge. */
  pins: PlanPin[];
}

/**
 * Die Plaene eines Mangels mit seinen Nadeln, in der Reihenfolge der ersten
 * Nadel. Welche Nadel auf welchem Plan steht, entscheidet services/planPins
 * (Nadel ohne eigenen Plan → Plan des Eintrags) — dieselbe Regel wie
 * Bildschirm und PDF. Plaene, die die RPC nicht mitliefert (im PDF
 * abgewaehlt, Bild fehlt, Nr. 182 nicht gefahren), fallen weg.
 */
export const planAnsichtenFuer = (
  item: Pick<FreimeldungPublicItem, 'planId' | 'planCoordinates'> | null | undefined,
  plaene: readonly FreimeldungPublicPlan[] | null | undefined,
): FreimeldungPlanAnsicht[] => {
  if (!item || !plaene || plaene.length === 0) return [];
  const quelle = { planId: item.planId, planCoordinates: item.planCoordinates };
  return planIdsVon(quelle).flatMap((planId) => {
    const plan = plaene.find((p) => p.id === planId);
    if (!plan) return [];
    const pins = pinsAufPlan(quelle, planId).map(({ x, y, planId: id }) => ({ x, y, planId: id }));
    return pins.length > 0 ? [{ plan, pins }] : [];
  });
};

/** Beschriftung des Plan-Knopfs an der Mangelkarte; leer = kein Knopf. */
export const planKnopfText = (anzahlPlaene: number, lang: Sprache = 'de'): string => {
  const t = texteFuer(lang);
  if (anzahlPlaene <= 0) return '';
  return anzahlPlaene === 1 ? t.planZeigen : t.planZeigenMehrere(anzahlPlaene);
};

/** Zoomstufen der Planansicht (Faktor auf die Breite des Sichtfensters). */
export const PLAN_ZOOM_STUFEN: readonly number[] = [1, 1.5, 2, 3, 4];
/** Start: doppelt — auf dem Handy ist ein ganzer Plan zu klein, um die Nadel zu verorten. */
export const PLAN_ZOOM_START = 2;

/** Naechste Zoomstufe in Richtung +1 (groesser) oder −1 (kleiner); am Ende bleibt sie stehen. */
export const naechsterPlanZoom = (zoom: number, richtung: 1 | -1): number => {
  const stufen = PLAN_ZOOM_STUFEN;
  if (richtung > 0) return stufen.find((s) => s > zoom + 1e-9) ?? stufen[stufen.length - 1];
  return [...stufen].reverse().find((s) => s < zoom - 1e-9) ?? stufen[0];
};

/**
 * Scrollposition, die einen Punkt (Anteil 0–1 der Planflaeche) in die Mitte
 * des Sichtfensters legt — an den Raendern begrenzt, nie negativ.
 */
export const scrollAufPunkt = (
  anteil: { x: number; y: number },
  flaeche: { breite: number; hoehe: number },
  sicht: { breite: number; hoehe: number },
): { left: number; top: number } => {
  const begrenze = (wert: number, max: number): number => Math.min(Math.max(0, wert), Math.max(0, max));
  const ax = Number.isFinite(anteil.x) ? anteil.x : 0.5;
  const ay = Number.isFinite(anteil.y) ? anteil.y : 0.5;
  return {
    left: begrenze(ax * flaeche.breite - sicht.breite / 2, flaeche.breite - sicht.breite),
    top: begrenze(ay * flaeche.hoehe - sicht.hoehe / 2, flaeche.hoehe - sicht.hoehe),
  };
};

// ─── Fehlertexte ─────────────────────────────────────────────────────────────

/**
 * Meldungen zu den SQLSTATEs von `report_freimeldung_melden`.
 * P0002 behandelt der Hook als „Link ungueltig" (ganze Seite), der Text steht
 * hier trotzdem, damit ein Fehlerkasten nie leer bleibt. Bei 22023 kommt der
 * Klartext der Datenbank durch — er benennt das abgelehnte Feld.
 */
export const fehlerTextFuer = (code: string | null, message: string, lang: Sprache = 'de'): string => {
  const t = texteFuer(lang);
  switch (code) {
    case 'P0002':
      return t.ungueltig;
    case 'P0001':
      return t.fehlerErledigt;
    case '54000':
      return t.fehlerZuViele;
    case '22023':
      return (message || '').trim() || t.fehlerAllgemein;
    default:
      return t.fehlerAllgemein;
  }
};

/**
 * Text zu einem gescheiterten Foto-Upload. Die Route antwortet mit 503 und
 * „Migration Nr. 109 fehlt", solange das SQL nicht gefahren ist — dem Melder
 * sagt das nichts, er bekommt den sachlichen Satz.
 */
export const fotoFehlerText = (message: string, lang: Sprache = 'de'): string => {
  const t = texteFuer(lang);
  const m = (message || '').trim();
  if (/migration/i.test(m)) return t.fotoNichtEingerichtet;
  return m || t.fotoFehlgeschlagen;
};

// ─── Anzeige ─────────────────────────────────────────────────────────────────

/**
 * YYYY-MM-DD → „16.09.2026" (de) bzw. „16/09/2026" (en), ohne Zeitzonen-Umweg.
 * Ein ISO-Zeitstempel wird auf das Datum gekuerzt, alles Unlesbare bleibt stehen.
 */
export const formatDatum = (iso: string | null | undefined, lang: Sprache = 'de'): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return lang === 'en' ? `${m[3]}/${m[2]}/${m[1]}` : `${m[3]}.${m[2]}.${m[1]}`;
};

/** ISO-Zeitstempel → „16.09.2026, 07:41" in Ortszeit; Unlesbares unveraendert. */
export const formatZeitpunkt = (iso: string | null | undefined, lang: Sprache = 'de'): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const zwei = (n: number) => String(n).padStart(2, '0');
  const datum = lang === 'en'
    ? `${zwei(d.getDate())}/${zwei(d.getMonth() + 1)}/${d.getFullYear()}`
    : `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}`;
  return `${datum}, ${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
};

// ─── sessionStorage ──────────────────────────────────────────────────────────

/**
 * Was ein Neuladen ueberlebt: die Angaben zur Person. NIE das Foto (weder
 * DataURL noch URL) und nie der gewaehlte Mangel — wer die Seite neu laedt,
 * faengt bei der Liste an und sieht den frischen Stand.
 */
export interface KontaktZustand {
  firma: string;
  name: string;
  email: string;
  phone: string;
}

export const LEERER_KONTAKT: KontaktZustand = { firma: '', name: '', email: '', phone: '' };

export const speicherSchluessel = (token: string): string => `osia.freimeldung.${token}`;

export const serialisiereZustand = (z: KontaktZustand): string =>
  JSON.stringify({
    version: 1,
    firma: z.firma || '',
    name: z.name || '',
    email: z.email || '',
    phone: z.phone || '',
  });

const istString = (v: unknown): v is string => typeof v === 'string';

/** Liest tolerant: kaputtes JSON, fremde Version oder fehlende Felder ergeben null bzw. ''. */
export const parseZustand = (raw: string | null | undefined): KontaktZustand | null => {
  if (!raw) return null;
  let obj: unknown;
  try {
    obj = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const z = obj as Record<string, unknown>;
  if (z.version !== 1) return null;
  return {
    firma: istString(z.firma) ? z.firma : '',
    name: istString(z.name) ? z.name : '',
    email: istString(z.email) ? z.email : '',
    phone: istString(z.phone) ? z.phone : '',
  };
};

/** Kontaktangaben aus dem Formular — fuer „Weiteren Mangel melden" und den Speicher. */
export const kontaktAus = (f: FreimeldungFormular): KontaktZustand => ({
  firma: f.firma || '',
  name: f.name || '',
  email: f.email || '',
  phone: f.phone || '',
});

/**
 * Naechster Mangel: Firma und Kontakt bleiben stehen (meist meldet dieselbe
 * Person mehrere Maengel), Kommentar und Foto sind weg — die gehoeren zum
 * einzelnen Mangel.
 */
export const naechsterMangel = (f: FreimeldungFormular): FreimeldungFormular => ({
  ...LEERES_FORMULAR,
  ...kontaktAus(f),
});
